/**
 * Matriz de permisos reutilizable (carpetas + archivos) con la explicación de
 * cada permiso visible debajo de la casilla (no solo en tooltip). La consumen el
 * editor de permisos por carpeta (permissions.js) y el editor de permisos por
 * usuario en Clientes (customers/permissions_drawer.js).
 *
 * El componente solo renderiza los checkboxes (Form.Items con nombres
 * compartidos "folder_" y "file_"); el Form y el guardado los provee el padre.
 * Granular (2026-10-01): sin «Cerrar»; una matriz vieja se muestra con la
 * herencia de toGranular y siempre se guarda con granular:true.
 * Los helpers matrixToFields / fieldsToPermissions traducen entre el shape de
 * Firestore ({documents, folders}) y los campos del formulario.
 */
import React from 'react';
import { Form, Checkbox, Card, Row, Col } from 'antd';
import { describePermission } from '../../lib/permission_labels';
import { toGranular, GRANULAR_DOC_FLAGS } from '../../lib/permission_granular';

const descStyle = {
  marginLeft: 24,
  marginTop: 2,
  color: 'rgba(0, 0, 0, 0.45)',
  fontSize: 12,
  lineHeight: 1.4,
};

// lockedReason: la casilla viene dada por la empresa o el global de carpeta;
// se muestra deshabilitada y con el motivo en vez de la explicación.
const Cell = ({ name, label, group, flag, lockedReason }) => (
  <Col xs={24} sm={12} style={{ marginBottom: 16 }}>
    <Form.Item name={name} valuePropName="checked" initialValue={false} style={{ marginBottom: 0 }}>
      <Checkbox disabled={!!lockedReason}><strong>{label}</strong></Checkbox>
    </Form.Item>
    <div style={descStyle}>{lockedReason || describePermission(group, flag)}</div>
  </Col>
);

const siNo = (v) => (v ? 'sí' : 'no');

// Abrir y cerrar carpetas en ESTA empresa (lib/closed_folder_perms): solo root
// la ve. `global` = lo que tendría sin decisión de la empresa (rol + usuario).
const CarpetasCard = ({ global }) => (
  <Card title="Abrir y cerrar carpetas (solo root)" size="small" style={{ marginBottom: 16 }}>
    <Row gutter={16}>
      {[['abrir', 'Abrir carpetas'], ['cerrar', 'Cerrar carpetas']].map(([k, label]) => (
        <Col xs={24} sm={12} style={{ marginBottom: 16 }} key={k}>
          <Form.Item name={`carpetas_${k}`} valuePropName="checked" initialValue={false} style={{ marginBottom: 0 }}>
            <Checkbox><strong>{label}</strong></Checkbox>
          </Form.Item>
          <div style={descStyle}>
            {`En esta empresa manda esta casilla. Por su rol y su usuario: ${siNo(global && global[k])}.`}
          </div>
        </Col>
      ))}
    </Row>
  </Card>
);

export const PermissionMatrix = ({ locked = {}, carpetas = null } = {}) => (
  <>
    {carpetas ? <CarpetasCard global={carpetas.global} /> : null}
    <Card title="Permisos a carpetas" size="small" style={{ marginBottom: 16 }}>
      <Row gutter={16}>
        <Cell name="folder_read" label="Leer" group="folders" flag="read" lockedReason={locked.folder_read} />
        <Cell name="folder_create" label="Crear" group="folders" flag="create" lockedReason={locked.folder_create} />
        <Cell name="folder_update" label="Editar" group="folders" flag="update" lockedReason={locked.folder_update} />
        <Cell name="folder_delete" label="Eliminar" group="folders" flag="delete" lockedReason={locked.folder_delete} />
        <Cell name="folder_download" label="Descargar" group="folders" flag="download" lockedReason={locked.folder_download} />
      </Row>
    </Card>

    <Card title="Permisos a archivos" size="small">
      <Row gutter={16}>
        <Cell name="file_read" label="Leer" group="documents" flag="read" lockedReason={locked.file_read} />
        <Cell name="file_create" label="Subir documentos" group="documents" flag="create" lockedReason={locked.file_create} />
        <Cell name="file_create_xml" label="Subir XML" group="documents" flag="createXml" lockedReason={locked.file_create_xml} />
        <Cell name="file_create_link" label="Vincular documentos" group="documents" flag="createLink" lockedReason={locked.file_create_link} />
        <Cell name="file_create_reference" label="Agregar referencias" group="documents" flag="createReference" lockedReason={locked.file_create_reference} />
        <Cell name="file_update" label="Aprobar archivos" group="documents" flag="update" lockedReason={locked.file_update} />
        <Cell name="file_disapprove" label="Desaprobar archivos" group="documents" flag="disapprove" lockedReason={locked.file_disapprove} />
        <Cell name="file_delete" label="Eliminar documentos" group="documents" flag="delete" lockedReason={locked.file_delete} />
        <Cell name="file_delete_xml" label="Eliminar XML" group="documents" flag="deleteXml" lockedReason={locked.file_delete_xml} />
        <Cell name="file_delete_reference" label="Eliminar referencias" group="documents" flag="deleteReference" lockedReason={locked.file_delete_reference} />
        <Cell name="file_delete_linked" label="Eliminar archivos vinculados" group="documents" flag="deleteLinked" lockedReason={locked.file_delete_linked} />
        <Cell name="file_accounting" label="Contabilizar" group="documents" flag="accounting" lockedReason={locked.file_accounting} />
        <Cell name="file_delete_accounting" label="Eliminar contabilización" group="documents" flag="deleteAccounting" lockedReason={locked.file_delete_accounting} />
        <Cell name="file_download" label="Descargar" group="documents" flag="download" lockedReason={locked.file_download} />
      </Row>
    </Card>
  </>
);

const FILE_FIELD = {
  read: 'file_read', create: 'file_create', createXml: 'file_create_xml', createLink: 'file_create_link',
  createReference: 'file_create_reference', update: 'file_update', disapprove: 'file_disapprove',
  delete: 'file_delete', deleteXml: 'file_delete_xml', deleteReference: 'file_delete_reference',
  deleteLinked: 'file_delete_linked', accounting: 'file_accounting',
  deleteAccounting: 'file_delete_accounting', download: 'file_download',
};
const FOLDER_FIELDS = ['read', 'create', 'update', 'delete', 'download'];

/** Permisos de Firestore → campos. Una matriz vieja se muestra con la herencia. */
export const matrixToFields = (perms) => {
  const d = toGranular({ documents: perms?.documents || {} }).documents;
  const f = perms?.folders || {};
  return [
    ...FOLDER_FIELDS.map((k) => ({ name: `folder_${k}`, value: !!f[k] })),
    ...GRANULAR_DOC_FLAGS.map((k) => ({ name: FILE_FIELD[k], value: !!d[k] })),
  ];
};

/** Campos → permisos de Firestore, siempre en formato granular. */
export const fieldsToPermissions = (v) => {
  const documents = { granular: true };
  GRANULAR_DOC_FLAGS.forEach((k) => { documents[k] = !!v[FILE_FIELD[k]]; });
  const folders = {};
  FOLDER_FIELDS.forEach((k) => { folders[k] = !!v[`folder_${k}`]; });
  return { documents, folders };
};

/** 'documents.read' → 'file_read'; 'folders.create' → 'folder_create'. Cerrar no tiene campo. */
export const fieldNameOf = (key) => {
  const [group, flag] = String(key).split('.');
  if (group === 'documents') return FILE_FIELD[flag] || null;
  if (group === 'folders') return FOLDER_FIELDS.includes(flag) ? `folder_${flag}` : null;
  return null;
};

/**
 * Al guardar una matriz de carpeta: lo bloqueado (lo da la empresa o el global)
 * conserva el valor previo de la matriz (la propia, o la heredada con que se
 * llenó el formulario), o false. Así la carpeta no «congela» algo que la
 * empresa después reduzca (spec 2026-10-06).
 */
export const mergeLocked = ({ values, locked, own }) => {
  const out = fieldsToPermissions(values);
  const ownG = own ? toGranular({ documents: own.documents || {} }).documents : {};
  Object.keys(locked || {}).forEach((key) => {
    if (!fieldNameOf(key)) return;
    const [group, flag] = key.split('.');
    out[group][flag] = group === 'documents' ? !!ownG[flag] : !!(own && own.folders && own.folders[flag]);
  });
  return out;
};

export default PermissionMatrix;
